import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import Icon, { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import LinkOutlinedL from '@app/components/posts/icons/LinkOutlinedL';
import removeCollectionFromPost from '@app/actions/media/removeCollectionFromPost';
import sendNotification from '@app/actions/notifications/send';

import {
    Flex,
    Button,
    Divider,
    Space,
    Table,
    ConfigProvider,
    Carousel,
    Tooltip,
    Tag,
    Modal,
    type TableColumnsType,
    type CarouselRef,
} from '@app/ui';
import combineActions from '@app/utils/combineActions';
import connect from '@app/utils/connect';
import classNames from '@app/ui/sports-components/utils/classNames';
import currencyFormat from '@app/utils/currencyFormat';
import { abbreviatedDecimal, percentage } from '@app/utils/formatters';
import shortCurrency from '@app/utils/shortCurrency';
import checkIfImage from '@app/utils/isImage';
import checkIfVideo from '@app/utils/isVideo';
import { toSportsCdnUrl } from '@app/utils/sportsCdnUrl';
import * as he from 'he';
import * as R from 'ramda';
import {
    generatePostImage,
    generatePostReport,
} from '@app/utils/generatePostReport';
import './PostModal.scss';
import CircleLogo from '@app/components/common/CircleLogo';
import {
    VIDEO_IMPRESSIONS,
    PREMIUM_COLLECTIONS,
    PREMIUM_POST_LEVEL_INSIGHTS,
} from '@app/constants/config';

import RenderMediaModal from './RenderMediaModal';
import PostCarouselNavigation from './PostCarouselNavigation';
import PostCarouselMetrics from './PostCarouselMetrics';
import {
    getCarouselExposureDividerLabel,
    getExposureAnnotationsForSlide,
} from './PostModalExposureDetails';
import {
    CarouselSlideWithPlatformPostId,
    getCarouselSlideLabel,
    sortCarouselSlidesBySlideNumber,
} from './postCarouselSlideUtils';

const getTruncatedText = (text: string, max = 20, sliceLen = 17) =>
    text.length > max ? `${text.slice(0, sliceLen)}...` : text;

const getStatsWithPercents = (base, items, formatter) =>
    items
        .map(([val, total]) =>
            total ? `${formatter(val)} (${percentage(val / total)})` : null,
        )
        .filter(Boolean);

const getRightsholderUrl = logos => {
    const mainLogo = logos.find(rh => rh.main);
    const logo = mainLogo || logos[0];
    return logo?.url ? toSportsCdnUrl(logo.url) : undefined;
};

const domNode = document.getElementById('social-post-detail-modal');
interface FixedDataType {
    key: React.Key;
    rightsholder?: {
        id: number;
        name: string;
        url: string;
    };
    rhId: string;
    placement: string;
    sponsor: string;
    mvp: string;
    smv: string;
}
const getFixedColumns = (
    t: (key: string) => string,
): TableColumnsType<FixedDataType> => [
    {
        title: '',
        dataIndex: 'rhId',
        fixed: true,
        width: 50,
        render: (_, record) => {
            const { url, name } = record.rightsholder;
            return (
                <Tooltip placement="top" title={name} zIndex={6000}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircleLogo url={url} name={name} />
                    </div>
                </Tooltip>
            );
        },
    },
    {
        title: t('socialMediaExposure.postModal.columns.placement'),
        dataIndex: 'placement',
        render: text =>
            text.length > 20 ? (
                <Tooltip placement="top" title={text} zIndex={6000}>
                    {getTruncatedText(text)}
                </Tooltip>
            ) : (
                text
            ),
    },
    {
        title: t('socialMediaExposure.postModal.columns.sponsor'),
        dataIndex: 'sponsor',
        render: text =>
            text.length > 20 ? (
                <Tooltip placement="top" title={text} zIndex={6000}>
                    {getTruncatedText(text)}
                </Tooltip>
            ) : (
                text
            ),
    },
    {
        width: 58,
        title: t('socialMediaExposure.postModal.columns.mvp'),
        dataIndex: 'mvp',
    },
    {
        title: t('socialMediaExposure.postModal.columns.smv'),
        dataIndex: 'smv',
    },
];

type PostModalProps = {
    shown: boolean;
    postId: number;
    posts: Record<number, any>;
    onClose: () => void;
    onCloseButton: () => void;
    onCollectionRemoved?: () => void;
    client: any;
    removeCollectionFromPost: (
        clientId: number,
        postId: number,
        collectionId: number,
    ) => Promise<boolean>;
    sendNotification: (
        message: string,
        config?: {
            context?: 'success' | 'danger' | 'warning' | 'error';
            description?: React.ReactNode;
        },
    ) => void;
    collections: Record<number, any>;
    filters: any;
    user: any;
};

type CarouselSlide = CarouselSlideWithPlatformPostId & {
    id?: number;
    cdnMediaUrl?: string;
};

const PostModal: React.FC<PostModalProps> = ({
    shown,
    postId,
    posts,
    onClose,
    onCloseButton,
    onCollectionRemoved,
    client,
    removeCollectionFromPost,
    sendNotification,
    collections,
    filters,
    user,
}) => {
    const { t } = useTranslation('measurement');
    const post = posts[postId];
    const [isDownloadingReport, setDownloadingReport] = useState(false);
    const [isDownloadingImage, setDownloadingImage] = useState(false);
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const carouselRef = useRef<CarouselRef>(null);
    const fixedColumns = useMemo(() => getFixedColumns(t), [t]);
    const carouselSlides = useMemo(
        () =>
            sortCarouselSlidesBySlideNumber<CarouselSlide>(
                post?.carouselSlides || [],
            ),
        [post?.carouselSlides],
    );
    const isCarousel = post?.carousel && carouselSlides.length > 1;
    const activeSlide =
        (isCarousel ? carouselSlides[activeSlideIndex] : post) || post;
    const activeSlideLabel = getCarouselSlideLabel(
        activeSlide,
        activeSlideIndex,
    );

    // Image and video utilities
    const video = useRef<HTMLVideoElement | null>(null);
    const isVideo = activeSlide?.mediaType?.id === 2;
    const checkVideoExt = checkIfVideo(activeSlide?.cdnMediaUrl);
    const isImage = activeSlide?.mediaType?.id === 1;
    const checkImgExt = checkIfImage(activeSlide?.cdnMediaUrl);

    useEffect(() => {
        setActiveSlideIndex(0);
        carouselRef.current?.goTo?.(0, false);
    }, [postId]);

    const onSelectCarouselSlide = (slideIndex: number) => {
        setActiveSlideIndex(slideIndex);
        carouselRef.current?.goTo?.(slideIndex, false);
    };

    useEffect(() => {
        if (!post) return;
        document.body.classList.add('-noscroll');
        return () => {
            document.body.classList.remove('-noscroll');
        };
    }, [post]);

    if (!post) return null;

    // post and user utilities
    const username = activeSlide?.socialAccount.username
        ? `@${activeSlide.socialAccount.username}`
        : null;
    const rawContent = activeSlide?.content
        ? he.decode(activeSlide.content)
        : '';
    const isTruncated = rawContent.length > 250;
    const truncatedText = rawContent.slice(0, 247);
    const platform = activeSlide?.socialPlatform?.toLowerCase() || '';
    const platformLogoName =
        platform == 'twitter' || platform == 'x' ? 'x-twitter' : platform;

    const {
        id: clientId,
        primaryColor,
        secondaryColor,
        currency: { code: currency },
        permissions,
    } = client;

    const canViewCollections = permissions.includes(PREMIUM_COLLECTIONS);
    const canViewInsights = permissions.includes(PREMIUM_POST_LEVEL_INSIGHTS);
    const removeCollectionTooltip = t(
        'socialMediaExposure.postModal.collections.removeTooltip',
    );

    const annotations = getExposureAnnotationsForSlide({
        isCarousel,
        post,
        carouselSlides,
        activeSlide,
    });
    const {
        impressions,
        videoViews,
        engagements,
        fullMediaValue,
        postUrl,
        hasPaidMetrics,
        paidEngagements,
        paidFullMediaValue,
        paidImpressions,
        paidVideoViews,
        organicEngagements,
        organicFullMediaValue,
        organicImpressions,
        organicVideoViews,
    } = post || {};

    const showImpressions =
        ((isVideo && client.permissions.includes(VIDEO_IMPRESSIONS)) ||
            isImage) &&
        !!impressions;

    const showVideoViews = isVideo && !!videoViews;

    const paid = hasPaidMetrics
        ? getStatsWithPercents(
              impressions,
              [
                  [paidImpressions, impressions],
                  [paidVideoViews, videoViews],
                  [paidEngagements, engagements],
                  [paidFullMediaValue, fullMediaValue],
              ],
              val => abbreviatedDecimal(val) || shortCurrency(val, 1, currency),
          )
        : [];

    const organic = hasPaidMetrics
        ? getStatsWithPercents(
              impressions,
              [
                  [organicImpressions, impressions],
                  [organicVideoViews, videoViews],
                  [organicEngagements, engagements],
                  [organicFullMediaValue, fullMediaValue],
              ],
              val => abbreviatedDecimal(val) || shortCurrency(val, 1, currency),
          )
        : [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const metrics = [
        ...(showImpressions
            ? [
                  {
                      label: t(
                          'socialMediaExposure.postModal.metrics.impressions',
                      ),
                      value: impressions.toLocaleString(),
                  },
              ]
            : []),
        ...(showVideoViews
            ? [
                  {
                      label: t(
                          'socialMediaExposure.postModal.metrics.videoViews',
                      ),
                      value: videoViews.toLocaleString(),
                  },
              ]
            : []),
        {
            label: t('socialMediaExposure.postModal.metrics.engagements'),
            value: engagements.toLocaleString(),
        },
        {
            label: t('socialMediaExposure.postModal.metrics.fmv'),
            value: currencyFormat(fullMediaValue, 2, currency),
        },
    ];

    const rightsholdersById = filters.rightsholders.list;
    const annotationsWithRHLogos = annotations.map(annotation => {
        const id = annotation.rightsholder.id;
        const logos = rightsholdersById[id]?.rightsholderLogos || [];
        const url = logos.length ? getRightsholderUrl(logos) : null;
        return R.assocPath(['rightsholder', 'url'], url, annotation);
    });

    const modalClasses = classNames('modal-overlay', {
        'modal-shown': shown,
    });

    const tableData: FixedDataType[] = annotationsWithRHLogos.map(
        (annotation, index) => ({
            key: index,
            rightsholder: annotation.rightsholder,
            rhId: annotation.rightsholder?.id.toString(),
            placement: annotation.placement?.name || '',
            sponsor: annotation.sponsor?.name || '',
            mvp: percentage(annotation.mvpScore),
            smv: currencyFormat(annotation.sponsorMediaValue, 2, currency),
        }),
    );

    // 2. Calculate total sponsor media value
    const totalSmvValue = annotationsWithRHLogos.reduce(
        (sum, annotation) => sum + annotation.sponsorMediaValue,
        0,
    );
    const formattedTotalSmv = currencyFormat(totalSmvValue, 2, currency);

    const handleRemoveCollection = async (
        collectionId: number,
        collectionName: string,
    ) => {
        const postIds = post.postIds || [post.id];

        try {
            await Promise.all(
                postIds.map(slidePostId =>
                    removeCollectionFromPost(
                        clientId,
                        slidePostId,
                        collectionId,
                    ),
                ),
            );

            sendNotification(
                t('socialMediaExposure.postModal.collections.removeSuccess'),
                {
                    context: 'success',
                    description: t(
                        'socialMediaExposure.postModal.collections.removeSuccessDesc',
                        {
                            collectionName,
                        },
                    ),
                },
            );
            onCollectionRemoved?.();
        } catch (_error) {
            sendNotification(
                t('socialMediaExposure.postModal.collections.removeError'),
                {
                    context: 'danger',
                },
            );
        }
    };

    const confirmRemoveCollection = (
        event: React.MouseEvent<HTMLElement>,
        collectionId: number,
        collectionName: string,
    ) => {
        event.preventDefault();

        Modal.confirm({
            title: t(
                'socialMediaExposure.postModal.collections.removeConfirmTitle',
            ),
            content: t(
                'socialMediaExposure.postModal.collections.removeConfirmContent',
                {
                    collectionName,
                },
            ),
            icon: (
                <i
                    className="fa-solid fa-triangle-exclamation"
                    style={{
                        color: 'rgba(212, 15, 125, 1)',
                        marginRight: '1.1rem',
                        fontSize: '1.2rem',
                        marginTop: '4px',
                    }}
                />
            ),
            okText: t(
                'socialMediaExposure.postModal.collections.removeConfirmOk',
            ),
            cancelText: t('socialMediaExposure.actions.cancel'),
            okButtonProps: {
                style: {
                    marginTop: '1rem',
                    backgroundColor: 'rgba(212, 15, 125, 1)',
                    borderColor: 'rgba(212, 15, 125, 1)',
                },
            },
            cancelButtonProps: {
                style: {
                    marginTop: '1rem',
                    backgroundColor: '#000',
                    borderColor: '#000',
                    color: '#fff',
                },
            },
            onOk: () => handleRemoveCollection(collectionId, collectionName),
        });
    };

    return ReactDOM.createPortal(
        <div
            className={modalClasses}
            style={{ zIndex: 1200 }}
            onClick={onClose}
        >
            <div className="modal-v2-wrapper">
                <Button
                    className="button-close-v2"
                    color="primary"
                    variant="outlined"
                    icon={<CloseOutlined />}
                    size={'middle'}
                    onClick={onCloseButton}
                />
                <Flex className="modal-v2">
                    {isCarousel ? (
                        <div className="modal-v2-carousel-media-stage">
                            <div className="custom-carousel-container modal-v2-post-carousel-container">
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Carousel: {
                                                arrowSize: 25,
                                                arrowOffset: 4,
                                                dotActiveWidth: 24,
                                                dotHeight: 6,
                                            },
                                        },
                                    }}
                                >
                                    <Carousel
                                        ref={carouselRef}
                                        infinite={false}
                                        arrows
                                        dots
                                        beforeChange={() => {
                                            video.current?.pause?.();
                                        }}
                                        afterChange={setActiveSlideIndex}
                                    >
                                        {carouselSlides.map((slide, index) => (
                                            <div key={slide.id || index}>
                                                {RenderMediaModal(
                                                    slide,
                                                    checkIfImage(
                                                        slide?.cdnMediaUrl,
                                                    ),
                                                    checkIfVideo(
                                                        slide?.cdnMediaUrl,
                                                    ),
                                                    index === activeSlideIndex
                                                        ? video
                                                        : null,
                                                    t,
                                                )}
                                            </div>
                                        ))}
                                    </Carousel>
                                </ConfigProvider>
                            </div>
                            <PostCarouselNavigation
                                activeSlideIndex={activeSlideIndex}
                                activeSlideLabel={activeSlideLabel}
                                totalSlides={carouselSlides.length}
                                onChange={onSelectCarouselSlide}
                                showButtons={false}
                            />
                        </div>
                    ) : (
                        RenderMediaModal(
                            activeSlide,
                            checkImgExt,
                            checkVideoExt,
                            video,
                            t,
                        )
                    )}
                    <Flex className="modal-v2-actions">
                        <Flex gap={8}>
                            <Button
                                className="download-button-v2"
                                color="primary"
                                variant="outlined"
                                icon={<DownloadOutlined />}
                                size="small"
                                loading={isDownloadingReport}
                                onClick={() =>
                                    generatePostReport({
                                        post: activeSlide,
                                        videoRef: video,
                                        username,
                                        content: rawContent,
                                        annotationsWithRHLogos,
                                        currency,
                                        platform,
                                        primaryColor,
                                        secondaryColor,
                                        user,
                                        setDownloading: setDownloadingReport,
                                    })
                                }
                            >
                                {t(
                                    'socialMediaExposure.postModal.actions.download',
                                )}
                            </Button>
                            <Button
                                className="download-button-v2"
                                color="primary"
                                variant="outlined"
                                icon={<DownloadOutlined />}
                                size="small"
                                loading={isDownloadingImage}
                                data-test="post-download-jpeg"
                                onClick={() =>
                                    generatePostImage({
                                        post: activeSlide,
                                        videoRef: video,
                                        setDownloading: setDownloadingImage,
                                    })
                                }
                            >
                                {t(
                                    'socialMediaExposure.postModal.actions.downloadJpeg',
                                )}
                            </Button>
                        </Flex>
                        <a
                            href={activeSlide.postUrl || postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="-block"
                            data-test="post-link"
                        >
                            <Icon component={LinkOutlinedL} />
                        </a>
                    </Flex>
                    {canViewCollections && (
                        <ConfigProvider
                            theme={{
                                token: {
                                    colorBorder: '#009FDF',
                                },
                            }}
                        >
                            <Flex className="modal-v2-collections">
                                {post.collectionIds &&
                                    post.collectionIds.map(id => {
                                        const name = R.path(
                                            [id, 'name'],
                                            collections,
                                        );
                                        return (
                                            <Tag
                                                className="tag-v2"
                                                key={id}
                                                closeIcon={
                                                    <Tooltip
                                                        title={
                                                            removeCollectionTooltip
                                                        }
                                                        zIndex={6000}
                                                    >
                                                        <span>
                                                            <CloseOutlined />
                                                        </span>
                                                    </Tooltip>
                                                }
                                                onClose={event =>
                                                    confirmRemoveCollection(
                                                        event,
                                                        id,
                                                        name,
                                                    )
                                                }
                                            >
                                                {name}
                                            </Tag>
                                        );
                                    })}
                            </Flex>
                        </ConfigProvider>
                    )}
                    <Flex className="modal-v2-caption-container">
                        <span className="modal-v2-caption-icon">
                            <i
                                style={{ color: '#00000073' }}
                                className={`photo-stream__card-block-social-icon fa fab-fa6 fa-${platformLogoName} -m-r-2`}
                            />
                        </span>
                        <div className="modal-v2-caption-content">
                            {username && (
                                <span className="gds-text--bold v2-username">
                                    {username}
                                </span>
                            )}
                            <span>
                                {isTruncated ? (
                                    <>
                                        &quot;{truncatedText}&quot;
                                        <Tooltip
                                            title={rawContent}
                                            zIndex={6000}
                                        >
                                            <span className="caption-ellipsis">
                                                ...
                                            </span>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <>&quot;{rawContent}&quot;</>
                                )}
                            </span>
                        </div>
                    </Flex>
                    {canViewInsights && (
                        <>
                            {isCarousel ? (
                                <PostCarouselMetrics
                                    activeSlideIndex={activeSlideIndex}
                                    currency={currency}
                                    carouselSlides={carouselSlides}
                                    t={t}
                                    onSelectSlide={onSelectCarouselSlide}
                                />
                            ) : (
                                <Flex className="modal-v2-stats-data">
                                    <Flex className="modal-v2-stats-header-row">
                                        {metrics.map(({ label }, index) => (
                                            <Space
                                                className="modal-v2-stats-header"
                                                key={label || index}
                                            >
                                                {label}
                                            </Space>
                                        ))}
                                    </Flex>
                                    <Divider className="modal-v2-divider" />
                                    <Flex className="modal-v2-stats-header-row">
                                        {metrics.map(({ value }, index) => (
                                            <Space
                                                className="modal-v2-stats-body"
                                                key={index}
                                            >
                                                {value}
                                            </Space>
                                        ))}
                                    </Flex>
                                    {hasPaidMetrics && (
                                        <>
                                            <Space className="modal-v2-stats-header">
                                                {t(
                                                    'socialMediaExposure.postModal.metrics.paid',
                                                )}
                                            </Space>
                                            <Flex className="modal-v2-stats-header-row">
                                                {paid.map((value, index) => (
                                                    <Space
                                                        className="modal-v2-stats-body"
                                                        key={`paid-${index}`}
                                                    >
                                                        {value}
                                                    </Space>
                                                ))}
                                            </Flex>
                                            <Space className="modal-v2-stats-header">
                                                {t(
                                                    'socialMediaExposure.postModal.metrics.organic',
                                                )}
                                            </Space>
                                            <Flex className="modal-v2-stats-header-row">
                                                {organic.map((value, index) => (
                                                    <Space
                                                        className="modal-v2-stats-body"
                                                        key={`organic-${index}`}
                                                    >
                                                        {value}
                                                    </Space>
                                                ))}
                                            </Flex>
                                        </>
                                    )}
                                </Flex>
                            )}
                            <ConfigProvider
                                theme={{
                                    token: {
                                        fontSize: 12,
                                        fontFamily: 'pnregular',
                                    },
                                    components: {
                                        Table: {
                                            cellFontSizeSM: 12,
                                            headerBg: '#e1effa',
                                            borderColor: '#0000000F',
                                        },
                                    },
                                }}
                            >
                                {isCarousel && (
                                    <Divider className="modal-v2-divider">
                                        {getCarouselExposureDividerLabel(
                                            activeSlideLabel,
                                        )}
                                    </Divider>
                                )}
                                <Table<FixedDataType>
                                    className="table-v2"
                                    columns={fixedColumns}
                                    dataSource={tableData}
                                    pagination={false}
                                    scroll={{ y: 125 }}
                                    bordered
                                    size="small"
                                    rowHoverable={false}
                                    summary={() => (
                                        <Table.Summary fixed>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell
                                                    className="table-v2-total"
                                                    index={0}
                                                >
                                                    {t(
                                                        'socialMediaExposure.postModal.total',
                                                    )}
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell
                                                    index={1}
                                                    align="center"
                                                />
                                                <Table.Summary.Cell
                                                    index={2}
                                                    align="center"
                                                />
                                                <Table.Summary.Cell
                                                    index={3}
                                                    align="center"
                                                />
                                                <Table.Summary.Cell
                                                    className="table-v2-smv"
                                                    index={4}
                                                    align="center"
                                                >
                                                    {formattedTotalSmv}
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    )}
                                />
                            </ConfigProvider>
                        </>
                    )}
                </Flex>
            </div>
        </div>,
        domNode,
    );
};

export default connect(
    ['client', 'filters', 'user'],
    combineActions({
        removeCollectionFromPost,
        sendNotification,
    }),
)(PostModal);
